import clone from 'lodash/clone'
import indexOf from 'lodash/indexOf'
import { isLottieCandidate, detectLottieRenderable } from '../../LottiePlayer/lottieDetection'

/**
 * Per-asset Lottie decision cache, keyed by `uid|updated_at`, so scrolling,
 * re-render, or re-opening the picker never re-sniffs the same asset. Module-level
 * on purpose (mirrors the AMV2AssetPickerModal viewHelper) — a Lottie decision is
 * immutable for a given asset version.
 */
const lottieDecisionCache = new Map<string, boolean>()

/**
 * Gate 1 (sync pair match) + gate 2 (async content sniff) for a single asset,
 * reusing the shared venus `lottieDetection` util. dotLottie ZIP short-circuits
 * inside `detectLottieRenderable` (no fetch); JSON pairs run the head/tail scan.
 * The `src` is the plain delivery `asset.url` — the legacy picker's `getAssetType`
 * never rewrites the URL, so a Lottie keeps its raw URL and the sniff reads exactly
 * the bytes the player will later fetch. Never throws; any failure → `false` (icon),
 * so a non-Lottie asset always falls back to the existing image/icon render.
 */
export const resolveIsLottie = async (asset, signal?: AbortSignal): Promise<boolean> => {
  if (!isLottieCandidate(asset)) return false
  const src = Array.isArray(asset.url) ? asset.url[0] : asset.url
  if (!src) return false

  const cacheKey = `${asset.uid}|${asset.updated_at ?? ''}`
  const cached = lottieDecisionCache.get(cacheKey)
  if (cached !== undefined) return cached

  const rawMime = Array.isArray(asset.content_type) ? asset.content_type[0] : asset.content_type
  const mime = String(rawMime ?? '').toLowerCase()
  const rawName = asset.file_name ?? asset.name ?? asset.filename ?? ''
  const nameStr = Array.isArray(rawName) ? rawName[0] ?? '' : rawName
  const extension = String(nameStr).split('.').pop()?.toLowerCase() ?? ''

  try {
    // Intentionally NOT passing `fileSize`. This mirrors dam-ui's `useLottieRenderDecision`
    // (asset-listing + trash) exactly — both call `detectLottieRenderable` with only
    // { mime, extension, src, signal }, so the JSON content gate runs the same Range
    // head-scan tier on every surface and a file can never validate differently across them.
    const decision = await detectLottieRenderable({ mime, extension, src, signal })
    const isLottie = decision === 'lottie-json' || decision === 'dotlottie-zip'
    // Don't cache a result produced from an aborted sniff — it may be a false
    // negative ('defer-manual' when the range fetch was cancelled). Re-sniff next time.
    if (!signal?.aborted) lottieDecisionCache.set(cacheKey, isLottie)
    return isLottie
  } catch {
    return false
  }
}

/**
 * Attaches a resolved `isLottie` boolean to each asset in place. Candidates are
 * sniffed in parallel; non-candidates resolve synchronously to `false`, so a page
 * with no Lottie assets adds no network cost. The same `signal` cancels every
 * in-flight sniff when the picker closes or the query changes.
 */
export const attachLottieFlags = async (assets, signal?: AbortSignal) => {
  if (!Array.isArray(assets) || assets.length === 0) return assets
  await Promise.all(
    assets.map(async asset => {
      asset.isLottie = await resolveIsLottie(asset, signal)
    })
  )
  return assets
}

export const formatTime = (date) => {
  date = new Date(date);
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours %= 12;
  hours = hours < 10 ? `0${hours}` : hours || 12;
  minutes = minutes < 10 ? `0${minutes}` : minutes;

  const strTime = `${hours}:${minutes} ${ampm}`;

  return (
    new Date(date).toString().split(" ").slice(1, 4).join(" ") +
    ", " +
    strTime
  );
};

export const getAssetType = (content_type) => {
  let assetType = 'Document'
  if (content_type) {
    switch (true) {
      case content_type.indexOf('image') > -1:
        assetType = null;
        break
      case content_type.indexOf('folder') > -1:
        assetType = 'Group'
        break
      case content_type.indexOf('pdf') > -1:
        assetType = 'Pdf'
        break
      case content_type.indexOf('excel') > -1:
      case content_type.indexOf('presentation') > -1:
      case content_type.indexOf('document') > -1:
      case content_type.indexOf('json') > -1:
      case content_type.indexOf('text/plain') > -1:
      case content_type.indexOf('text/html') > -1:
        assetType = 'Document'
        break
      case content_type.indexOf('zip') > -1:
        assetType = 'Archive'
        break
      case content_type.indexOf('video') > -1:
        assetType = 'Video'
        break
      case content_type.indexOf('audio') > -1:
        assetType = 'Audio'
        break
    }
    if (content_type.indexOf('image/tiff') > -1) {
      assetType = 'Document'
    }

    return assetType
  }

  return assetType
}

const convertFiletype = function (fileArray, onlyImage) {
  if (fileArray && fileArray.length && typeof fileArray === 'object' && !onlyImage) {
    for (var i = 0; i < fileArray.length; i++) {
      fileArray[i] = fileArray[i].trim();
    }
  } else if (fileArray && fileArray.length && !onlyImage) {
    fileArray = fileArray.split(/[ ,]+/);
  } else if (onlyImage) {
    return "image/*";
  } else {
    return;
  }

  var temparray = clone(fileArray);
  if (temparray) {
    for (var j = 0; j <= temparray.length - 1; j++) {
      temparray[j] = temparray[j].trim();
      if (j === 0) {
        temparray[j] = "." + temparray[j];
      } else {
        temparray[j] = " ." + temparray[j];
      }
    }
  }
  return temparray.toString();
};

const checkFileTypeValidation = function (asset, extensions) {
  var acceptFileTypes = extensions.split(/[ ,]+/);
  var getExtension = Array.isArray(asset.filename) ? asset.filename[0].split(".") : asset.filename.split(".");
  var sExtension = getExtension[getExtension.length - 1];

  if (getExtension.length > 1 && acceptFileTypes.length) {
    if (indexOf(acceptFileTypes, sExtension) >= 0) {
      return false;
    } else {
      asset.fileTypeError = true;
      var filetypes = convertFiletype(acceptFileTypes, false);
      // asset.errorMessage += "Unable to upload file. Permitted file types are " + filetypes + ".";
      asset.errorMessage += "Permitted file types are " + filetypes + ".";
      return true;
    }
  }
}

const checkFileSizeValidation = function (asset, minSizeProp, maxSizeProp) {
  minSizeProp = minSizeProp * 1024 * 1024
  maxSizeProp = maxSizeProp * 1024 * 1024
  var expectedFileMinSize = (minSizeProp === "" || !minSizeProp) ? 0 : minSizeProp;
  var expectedFileMaxSize = (maxSizeProp === "" || !maxSizeProp) ? 734003200 : maxSizeProp;
  var OgFilesizeinMb = asset.file_size;

  if (!OgFilesizeinMb || (OgFilesizeinMb <= expectedFileMaxSize) && (OgFilesizeinMb >= expectedFileMinSize)) {
    return false;
  } else {
    var minSize = (minSizeProp / (1024 * 1024)) || 0,
      maxSize = (maxSizeProp === null || !maxSizeProp) ? 700 : (maxSizeProp / (1024 * 1024));
    asset.errorMessage += "The file size should be within " + minSize + " MB and " + maxSize + " MB.";
    return true;
  }
};

export const checkFileValidation = (asset, filter) => {
  asset.errorMessage = "";
  var fileTypeError: any = false, fileSizeError = false;
  if (!asset.is_dir) {

    if (filter.extensions)
      fileTypeError = checkFileTypeValidation(asset, filter.extensions);

    if (filter.minSize || filter.maxSize)
      fileSizeError = checkFileSizeValidation(asset, filter.minSize, filter.maxSize);

    asset.err = (fileSizeError || fileTypeError);
    if (asset.err) {
      asset.errorMessage = "Unable to select file." + asset.errorMessage;
    }
  }
  if (asset.errorMessage) {
    asset.invalid_asset = true
  } else {
    asset.invalid_asset = false
  }
}

export const getAssetsToBeSelected = (assets, selectedAssets, initiallySelectedAssets?: string[]) => {
  const assetsToBeSelected = [];
  if(!initiallySelectedAssets) {
    return assetsToBeSelected;
  }
  assets.forEach((asset: any) => {
    if(asset.invalid_asset || !initiallySelectedAssets?.includes(asset.uid) || selectedAssets.some(selectedAsset => selectedAsset.uid === asset.uid)) {
      return;
    }
    assetsToBeSelected.push(asset);
  })
  return assetsToBeSelected;
}